import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney as formatShopMoney } from '@theme/money-formatting';

// The stored money format can omit a symbol; product cards must still name the currency.
function formatMoney(value, format, currency) {
  const formatted = formatShopMoney(value, format, currency);
  const symbol = new Intl.NumberFormat(document.documentElement.lang || 'tr', { style: 'currency', currency }).formatToParts(0).find(part => part.type === 'currency')?.value || currency;
  return formatted.includes(symbol) || formatted.includes(currency) ? formatted : `${formatted} ${symbol}`;
}

/**
 * Shop-wide "added to bag" toast.
 *
 * On the reference design.com, adding to bag does NOT open a cart drawer — a persistent panel
 * appears top-right with the confirmation, a "View bag (N)" control and a
 * "Style with" cross-sell carousel. This element reproduces that and suppresses
 * the theme's own auto-open behaviour site-wide. It dismisses
 * itself after five seconds unless it is being hovered or read.
 *
 * It also owns the shared cart client: `addVariant()` is
 * public so `assets/giesto-store-size-picker.js` can add from a collection card
 * without a second implementation of `/cart/add`.
 *
 * Suppression mechanism (verified against `assets/cart-drawer.js`):
 * `CartDrawerComponent#handleCartLinesUpdate` gates the auto-open on
 * `this.hasAttribute('auto-open')`, where `this` is `<cart-drawer-component>`,
 * NOT `<theme-drawer id="cart-drawer">`. `snippets/cart-drawer.liquid` prints
 * the attribute on `<cart-drawer-component>` when `settings.auto_open_cart_drawer`
 * is on. The attribute is read at event time rather than at registration time,
 * so removing it here — regardless of module execution order — is sufficient.
 * A MutationObserver re-strips it because the drawer's inner markup is replaced
 * by the Section Rendering API on every cart update.
 */

const DRAWER_OPEN_EVENT = 'theme-drawer:open';
const RECOMMENDATION_INTENT = 'related';

/**
 * How long the panel stays up on its own before dismissing itself.
 *
 * The reference calls its toast with `{ autoClose: u ? 1e4 : 9e3, pauseOnHover:
 * u, pauseOnFocusLoss: false, closeOnClick: false, hideProgressBar: true }`,
 * where `u` is "the Style with carousel has something to show". So the panel
 * that carries the cross-sell rail gets the longer timer and pauses under the
 * pointer; the bare confirmation gets the shorter one and does not.
 */
const AUTO_CLOSE_MS = 9000;
const AUTO_CLOSE_WITH_CROSS_SELL_MS = 10000;

/**
 * Extracts the numeric variant id from either a bare id or a `gid://` global id.
 * @param {unknown} merchandiseId
 * @returns {string}
 */
function variantIdOf(merchandiseId) {
  const raw = String(merchandiseId ?? '');
  const tail = raw.split('/').pop();
  return tail ?? raw;
}

/**
 * Adds a `width` parameter to a Shopify CDN image URL.
 *
 * The cart/recommendations JSON returns protocol-relative CDN URLs
 * (`//cdn.shopify.com/...`), which `new URL(src, origin)` resolves. Only
 * http(s) is rewritten — appending a query string to a `data:` URI would
 * corrupt its payload.
 *
 * @param {string | null | undefined} src
 * @param {number} width
 * @returns {string}
 */
function sizedImage(src, width) {
  if (!src) return '';

  try {
    const url = new URL(src, window.location.origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return src;

    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
}

/**
 * Appends a `.js` suffix to a Shopify route so it returns JSON.
 * @param {string | undefined} route
 * @param {string} fallback
 * @returns {string}
 */
function jsonRoute(route, fallback) {
  const base = route || fallback;
  return base.endsWith('.js') || base.endsWith('.json') ? base : `${base.replace(/\/$/, '')}.js`;
}

class GiestoStoreToast extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MutationObserver | null} */
  #autoOpenObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /**
   * Increments on every cart event. Any in-flight cart/recommendation response
   * whose token is stale is discarded, so rapid updates can't repaint the
   * panel with an older cart snapshot.
   * @type {number}
   */
  #token = 0;

  /** @type {Map<string, { product: any, variant: any }>} */
  #entries = new Map();

  /** @type {any[] | null} */
  #fallbackProducts = null;

  /** Auto-dismiss timer handle. 0 when no dismissal is pending. */
  #autoCloseTimer = 0;

  /** When the pending dismissal is due, as a timestamp. 0 when none is armed. */
  #autoCloseEndsAt = 0;

  /** Milliseconds left on a paused countdown. 0 when it is running or unused. */
  #autoCloseRemaining = 0;

  /** @type {number} */
  #dismissTimer = 0;

  /** @type {((event: TransitionEvent) => void) | null} */
  #dismissTransitionEnd = null;

  /** @type {number} */
  #snapTimer = 0;

  /** @type {{ pointerId: number, startX: number, startY: number, lastX: number, startTime: number, active: boolean } | null} */
  #drag = null;

  /** Parsed once from the Liquid-rendered config: threshold plus every string. */
  #delivery = null;

  /** The page's own inline overflow/padding, restored when the panel closes. */
  #scrollLock = null;

  /** The strip heading's resting text, restored when the pointer leaves it. */
  #stripRestingTitle = '';

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  #desktop = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    // Re-entrant by contract: a move in the DOM fires disconnect + connect, and
    // the theme's morph can reconnect this element without a disconnect at all.
    this.disconnectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#suppressDrawerAutoOpen();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
    document.addEventListener(DRAWER_OPEN_EVENT, this.#onDrawerOpen, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('click', this.#onClick, { signal });

    this.#reducedMotion.addEventListener('change', this.#onMotionPreferenceChange, { signal });

    // The reference's strip names whatever the pointer is over, and returns to
    // its resting heading when the pointer leaves the strip — not the tile.
    const carousel = this.#carousel;
    carousel?.addEventListener('pointerover', this.#onStripPoint, { signal });
    carousel?.addEventListener('focusin', this.#onStripPoint, { signal });
    carousel?.addEventListener('pointerleave', this.#onStripLeave, { signal });
    carousel?.addEventListener('focusout', this.#onStripLeave, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#autoOpenObserver?.disconnect();
    this.#autoOpenObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#clearAutoClose();
    this.#unlockScroll();
    this.#resetPanelState();
  }

  /* ---------------------------------------------------------------- refs -- */

  /** @returns {HTMLElement | null} */
  get #panel() {
    return this.querySelector('.gs-toast__panel');
  }

  /** @returns {HTMLElement | null} */
  get #column() {
    return this.querySelector('[data-gs-toast-column]');
  }

  /** @returns {HTMLElement | null} */
  get #carousel() {
    return this.querySelector('[data-gs-toast-carousel]');
  }

  /** @returns {HTMLElement | null} */
  get #cross() {
    return this.querySelector('[data-gs-toast-cross]');
  }

  /** @returns {HTMLElement | null} */
  get #bag() {
    return this.querySelector('[data-gs-toast-bag]');
  }

  /** @returns {HTMLElement | null} */
  get #bagCarousel() {
    return this.querySelector('[data-gs-toast-bag-carousel]');
  }

  /** @returns {HTMLTemplateElement | null} */
  get #cardTemplate() {
    const template = this.querySelector('[data-gs-toast-card-template]');
    return template instanceof HTMLTemplateElement ? template : null;
  }

  /** @returns {HTMLTemplateElement | null} */
  get #bagCardTemplate() {
    const template = this.querySelector('[data-gs-toast-bag-card-template]');
    return template instanceof HTMLTemplateElement ? template : null;
  }

  /* ------------------------------------------------- drawer suppression -- */

  #suppressDrawerAutoOpen() {
    if (!this.hasAttribute('data-suppress-cart-drawer')) return;

    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer) return;

    const strip = () => {
      drawer.removeAttribute('auto-open');
      for (const element of drawer.querySelectorAll('[auto-open]')) element.removeAttribute('auto-open');
    };

    strip();

    // Terminates: `removeAttribute` on an element that lacks the attribute
    // produces no mutation record, so the callback settles after one pass.
    this.#autoOpenObserver = new MutationObserver(strip);
    this.#autoOpenObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['auto-open'],
      childList: true,
      subtree: true,
    });
  }

  /* --------------------------------------------------------- cart events -- */

  /** @param {any} event */
  #onCartLinesUpdate = (event) => {
    // Claim the revision before the async cart action settles. Two quick adds
    // can resolve in the opposite order; the older promise must then be
    // ignored instead of replacing the newer cart preview.
    const token = ++this.#token;
    const promise = event?.promise instanceof Promise ? event.promise : Promise.resolve(null);

    if (event?.action !== 'add') {
      // Keep a visible preview honest after a quantity change or removal, but
      // do not reopen the confirmation or restart its dismissal timer.
      if (!this.hidden) {
        promise
          .then((result) => {
            if (result?.detail?.didError) return;
            return this.#refreshCartPreview(result, token);
          })
          .catch(() => {});
      }
      return;
    }

    const variantIds = Array.isArray(event.lines) ? event.lines.map((line) => variantIdOf(line?.merchandiseId)) : [];

    promise
      .then((result) => {
        if (result?.detail?.didError) return;
        return this.#present(variantIds, result, token);
      })
      .catch(() => {
        // The add failed — the theme surfaces its own error, stay quiet.
      });
  };

  /**
   * `theme-drawer:open` bubbles from every drawer, so scope this to the cart —
   * opening the menu or search drawer should leave the toast alone.
   * @param {Event} event
   */
  #onDrawerOpen = (event) => {
    if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
    this.#hide();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || this.hidden) return;
    this.#hide();
  };

  /* --------------------------------------------------------------- show -- */

  /**
   * @param {string[]} variantIds
   * @param {any} result
   * @param {number} token
   */
  async #present(variantIds, result, token) {
    const cart = await this.#getCartSnapshot(result);
    if (token !== this.#token) return;

    const items = cart.items;
    const added =
      items.find((item) => variantIds.includes(String(item?.variant_id))) ?? items[items.length - 1] ?? null;

    const title = String(added?.product_title ?? added?.title ?? '');
    this.#resetPanelState();
    this.#setMessage(title);
    this.#setCount(cart.count);
    this.#renderLine(added);
    this.#renderDelivery(cart);
    this.#clearCrossSell();

    this.#show();

    const productId = added?.product_id;
    if (productId == null) {
      return;
    }

    await this.#loadRecommendations(String(productId), token);
  }

  #show() {
    this.hidden = false;
    this.#lockScroll();

    // The open attribute drives the dimmer's fade and the column's slide, so
    // it has to land on a frame after the element stops being `hidden`.
    window.requestAnimationFrame(() => {
      if (this.hidden) return;
      this.setAttribute('data-gs-toast-open', '');
      this.#column?.focus({ preventScroll: true });
    });
  }

  #hide() {
    this.#clearAutoClose();
    this.removeAttribute('data-gs-toast-open');
    this.#unlockScroll();

    const column = this.#column;
    const done = () => {
      this.#resetPanelState();
      this.hidden = true;
    };

    if (!column || this.#reducedMotion.matches) {
      done();
      return;
    }

    // Let the closing transition run, but never wait on a `transitionend` that
    // a background tab will not deliver.
    if (this.#dismissTimer) window.clearTimeout(this.#dismissTimer);
    this.#dismissTimer = window.setTimeout(() => {
      this.#dismissTimer = 0;
      done();
    }, 420);
  }

  /* --------------------------------------------------------- scroll lock -- */

  /**
   * The reference locks the page behind the drawer and compensates for the
   * scrollbar it removes, so the layout does not jump sideways as it opens.
   */
  #lockScroll() {
    if (this.#scrollLock) return;
    const root = document.documentElement;
    const gap = window.innerWidth - root.clientWidth;

    this.#scrollLock = {
      overflow: root.style.overflow,
      paddingRight: root.style.paddingRight,
    };

    root.style.overflow = 'hidden';
    if (gap > 0) root.style.paddingRight = `${gap}px`;
  }

  #unlockScroll() {
    const saved = this.#scrollLock;
    if (!saved) return;
    this.#scrollLock = null;

    const root = document.documentElement;
    if (saved.overflow) root.style.overflow = saved.overflow;
    else root.style.removeProperty('overflow');
    if (saved.paddingRight) root.style.paddingRight = saved.paddingRight;
    else root.style.removeProperty('padding-right');
  }

  /* --------------------------------------------------------- auto-close -- */

  /** Stops the countdown and forgets where it had got to. */
  #clearAutoClose() {
    if (this.#autoCloseTimer) window.clearTimeout(this.#autoCloseTimer);
    this.#autoCloseTimer = 0;
    this.#autoCloseEndsAt = 0;
    this.#autoCloseRemaining = 0;
  }

  /** Whether the Style with rail is on the panel — the reference's `u`. */
  #hasCrossSell() {
    return !!this.#cross && !this.#cross.hasAttribute('hidden');
  }

  /**
   * No-op by design. The reference's panel never dismisses itself — it waits
   * for the close button, Escape, or a click on the dimmer. The countdown's
   * call sites are left in place so nothing else has to know that.
   * @param {number} _ms
   */
  #armAutoClose(_ms) {}

  #startAutoClose() {
    this.#clearAutoClose();
    if (this.hidden) return;

    // No reduced-motion guard. It used to stop the timer from ever starting,
    // which is why the panel sat there until it was dismissed by hand; the
    // reference runs the same timer for everyone. Reduced motion still governs
    // the panel's animation and its drag-to-dismiss (see `#canDrag`) — it just
    // no longer decides whether the panel goes away on its own.
    this.#armAutoClose(this.#hasCrossSell() ? AUTO_CLOSE_WITH_CROSS_SELL_MS : AUTO_CLOSE_MS);
  }

  /**
   * Holds the countdown where it stands — it does NOT start it over.
   *
   * The reference runs its close off a CSS animation on the toast's progress
   * bar: `{ animationDuration: `${autoClose}ms`, animationPlayState: running ?
   * 'running' : 'paused' }`, with the close fired when that animation ends.
   * `animation-play-state: paused` keeps the elapsed fraction, so leaving the
   * panel resumes the remainder rather than granting a fresh full duration.
   * `hideProgressBar: true` only hides the bar; it still drives the timing.
   *
   * A setTimeout cannot be paused, so the remaining time is measured and
   * re-armed with exactly that on resume.
   */
  #pauseAutoClose = () => {
    if (!this.#autoCloseTimer) return;

    window.clearTimeout(this.#autoCloseTimer);
    this.#autoCloseTimer = 0;
    this.#autoCloseRemaining = Math.max(0, this.#autoCloseEndsAt - Date.now());
    this.#autoCloseEndsAt = 0;
  };

  /** `pauseOnHover: u` — the bare confirmation does not hold under the pointer. */
  #onPointerEnter = () => {
    if (this.#hasCrossSell()) this.#pauseAutoClose();
  };

  #resumeAutoClose = () => {
    if (this.hidden) return;
    // `pauseOnHover` is the reference's `u` — only the panel that carries the
    // cross-sell rail holds itself open under the pointer. Focus is deliberately
    // not consulted: the reference passes `pauseOnFocusLoss: false` and has no
    // pause-on-focus at all, and treating focus as a hold was the other reason
    // the panel could stay up indefinitely.
    if (this.#hasCrossSell() && this.#panel?.matches(':hover')) return;
    if (this.#autoCloseTimer) return;

    // Carry on from where the pause left it; only a panel that has no countdown
    // at all gets a full one.
    if (this.#autoCloseRemaining > 0) this.#armAutoClose(this.#autoCloseRemaining);
    else this.#startAutoClose();
  };

  #onMotionPreferenceChange = () => {
    // Only the drag state is motion-dependent now; the timer runs either way, so
    // switching the preference mid-panel must not cancel it.
    if (this.#reducedMotion.matches) this.#resetPanelState();
  };

  #resetPanelState() {
    if (this.#dismissTimer) {
      window.clearTimeout(this.#dismissTimer);
      this.#dismissTimer = 0;
    }

    if (this.#snapTimer) {
      window.clearTimeout(this.#snapTimer);
      this.#snapTimer = 0;
    }

    const panel = this.#panel;
    const drag = this.#drag;
    this.#drag = null;
    if (panel && drag && panel.hasPointerCapture(drag.pointerId)) panel.releasePointerCapture(drag.pointerId);

    if (panel && this.#dismissTransitionEnd) panel.removeEventListener('transitionend', this.#dismissTransitionEnd);
    this.#dismissTransitionEnd = null;

    this.removeAttribute('data-gs-toast-dragging');
    this.removeAttribute('data-gs-toast-snapping');
    this.removeAttribute('data-gs-toast-dismissing');

    if (!panel) return;
    panel.inert = false;
    panel.removeAttribute('aria-hidden');
    panel.style.removeProperty('--gs-toast-drag-x');
    panel.style.removeProperty('--gs-toast-drag-scale');
    panel.style.removeProperty('--gs-toast-drag-opacity');
  }

  #dismissAfterDrag() {
    const panel = this.#panel;
    if (!panel || this.hidden) return;

    this.#clearAutoClose();
    this.removeAttribute('data-gs-toast-dragging');
    this.removeAttribute('data-gs-toast-snapping');
    this.setAttribute('data-gs-toast-dismissing', '');
    panel.inert = true;
    panel.setAttribute('aria-hidden', 'true');

    const finish = () => {
      if (!this.hasAttribute('data-gs-toast-dismissing')) return;
      this.#hide();
    };

    const onTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'transform') return;
      panel.removeEventListener('transitionend', onTransitionEnd);
      if (this.#dismissTransitionEnd === onTransitionEnd) this.#dismissTransitionEnd = null;
      finish();
    };

    this.#dismissTransitionEnd = onTransitionEnd;
    panel.addEventListener('transitionend', onTransitionEnd);
    this.#dismissTimer = window.setTimeout(() => {
      panel.removeEventListener('transitionend', onTransitionEnd);
      if (this.#dismissTransitionEnd === onTransitionEnd) this.#dismissTransitionEnd = null;
      finish();
    }, 340);
  }

  #snapBack() {
    const panel = this.#panel;
    if (!panel) return;

    this.removeAttribute('data-gs-toast-dragging');
    this.setAttribute('data-gs-toast-snapping', '');
    panel.style.setProperty('--gs-toast-drag-x', '0px');
    panel.style.setProperty('--gs-toast-drag-scale', '1');
    panel.style.setProperty('--gs-toast-drag-opacity', '1');

    if (this.#snapTimer) window.clearTimeout(this.#snapTimer);
    this.#snapTimer = window.setTimeout(() => {
      this.#snapTimer = 0;
      this.removeAttribute('data-gs-toast-snapping');
      this.#resumeAutoClose();
    }, 220);
  }

  #canDrag() {
    return !this.hidden && this.#desktop.matches && !this.#reducedMotion.matches;
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (!this.#canDrag() || !event.isPrimary) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const panel = this.#panel;
    if (!panel || !(event.target instanceof Element)) return;
    if (
      event.target.closest(
        'a, button, input, select, textarea, summary, [contenteditable], [data-gs-toast-carousel], [data-gs-toast-bag-carousel]'
      )
    ) {
      return;
    }

    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      startTime: performance.now(),
      active: false,
    };
    panel.setPointerCapture(event.pointerId);
    this.#pauseAutoClose();
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const drag = this.#drag;
    const panel = this.#panel;
    if (!drag || !panel || event.pointerId !== drag.pointerId) return;

    const dx = Math.max(0, event.clientX - drag.startX);
    const dy = Math.abs(event.clientY - drag.startY);
    drag.lastX = event.clientX;

    if (!drag.active) {
      if (dx < 8 || dx <= dy) return;
      drag.active = true;
      this.setAttribute('data-gs-toast-dragging', '');
    }

    event.preventDefault();
    const progress = Math.min(dx / Math.max(panel.clientWidth, 1), 1);
    panel.style.setProperty('--gs-toast-drag-x', `${dx}px`);
    panel.style.setProperty('--gs-toast-drag-scale', String(1 - 0.26 * progress));
    panel.style.setProperty('--gs-toast-drag-opacity', String(1 - 0.82 * progress));
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    this.#finishDrag(event.pointerId, event.clientX);
  };

  /** @param {PointerEvent} event */
  #onPointerCancel = (event) => {
    this.#finishDrag(event.pointerId, null, true);
  };

  /** @param {PointerEvent} event */
  #onLostPointerCapture = (event) => {
    this.#finishDrag(event.pointerId, null, true);
  };

  /**
   * @param {number} pointerId
   * @param {number | null} clientX
   * @param {boolean} [cancelled]
   */
  #finishDrag(pointerId, clientX, cancelled = false) {
    const drag = this.#drag;
    const panel = this.#panel;
    if (!drag || !panel || pointerId !== drag.pointerId) return;

    this.#drag = null;
    if (panel.hasPointerCapture(pointerId)) panel.releasePointerCapture(pointerId);

    if (!drag.active) {
      this.#resumeAutoClose();
      return;
    }

    if (cancelled) {
      this.#snapBack();
      return;
    }

    const distance = Math.max(0, (clientX ?? drag.lastX) - drag.startX);
    const progress = distance / Math.max(panel.clientWidth, 1);
    const velocity = distance / Math.max(performance.now() - drag.startTime, 1);

    if (progress >= 0.28 || (distance >= 80 && velocity > 0.45)) {
      this.#dismissAfterDrag();
      return;
    }

    this.#snapBack();
  }

  /* ------------------------------------------------------------ content -- */

  /**
   * @param {string} src - Already width-limited CDN URL, or '' for no image.
   */
  #setThumbnail(src) {
    const frame = this.querySelector('[data-gs-toast-thumb]');
    const image = this.querySelector('[data-gs-toast-thumb-image]');
    if (!(frame instanceof HTMLElement) || !(image instanceof HTMLImageElement)) return;

    if (src) {
      image.src = src;
    } else {
      // Not `src = ''`: an empty src resolves to the page URL and re-downloads
      // the document as if it were an image.
      image.removeAttribute('src');
    }

    frame.toggleAttribute('hidden', !src);
  }

  /** @param {string} productTitle */
  #setMessage(productTitle) {
    const target = this.querySelector('[data-gs-toast-message]');
    const template = this.dataset.messageTemplate || '{{ product_title }} was added to your bag';
    const titleToken = this.dataset.titleToken || '__ARK_TITLE__';
    const text = template.includes(titleToken)
      ? template.split(titleToken).join(productTitle)
      : template.replace(/\{\{\s*product_title\s*\}\}/g, productTitle);

    if (target) target.textContent = text;

    const status = this.querySelector('[data-gs-toast-status]');
    if (status) {
      // Clear-then-set so an identical message is re-announced. Deliberately a
      // timeout rather than requestAnimationFrame: rAF is starved in a
      // backgrounded tab, which would swallow the announcement entirely.
      status.textContent = '';
      window.setTimeout(() => {
        status.textContent = text;
      }, 50);
    }
  }

  /**
   * The shop's own money format, so every price in the panel reads the way the
   * rest of the storefront does.
   * @param {number} cents
   */
  #formatMoney(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'USD');
  }

  /* ------------------------------------------------- recommendation strip -- */

  /**
   * The strip's heading carries whatever the pointer or focus is on, with its
   * price beside it.
   * @param {Event} event
   */
  #onStripPoint = (event) => {
    if (!(event.target instanceof Element)) return;
    const slide = event.target.closest('.gs-toast__slide');
    if (!slide) return;

    const title = this.querySelector('[data-gs-toast-cross-title]');
    const price = this.querySelector('[data-gs-toast-cross-price]');
    if (!(title instanceof HTMLElement) || !(price instanceof HTMLElement)) return;

    if (!this.#stripRestingTitle) this.#stripRestingTitle = title.textContent || '';

    const name = slide.querySelector('[data-gs-card-title]')?.textContent || '';
    const value = slide.querySelector('[data-gs-card-value]')?.textContent || '';
    if (!name) return;

    title.textContent = name;
    price.textContent = value;
    price.hidden = !value;
  };

  /**
   * Restored when the pointer leaves the strip, not the individual tile —
   * moving between tiles must not flash the resting heading.
   * @param {Event} event
   */
  #onStripLeave = (event) => {
    const related = /** @type {FocusEvent & PointerEvent} */ (event).relatedTarget;
    const carousel = this.#carousel;
    if (related instanceof Node && carousel?.contains(related)) return;

    const title = this.querySelector('[data-gs-toast-cross-title]');
    const price = this.querySelector('[data-gs-toast-cross-price]');
    if (title instanceof HTMLElement && this.#stripRestingTitle) title.textContent = this.#stripRestingTitle;
    if (price instanceof HTMLElement) {
      price.textContent = '';
      price.hidden = true;
    }
  };

  /**
   * The line just added: its image, name, the variant's own options and its
   * price. The reference lists only this line — the panel confirms an add, it
   * is not a view of the bag.
   * @param {any} line
   */
  #renderLine(line) {
    const section = this.querySelector('[data-gs-toast-line]');
    if (!(section instanceof HTMLElement)) return;

    if (!line) {
      section.hidden = true;
      return;
    }

    const title = String(line.product_title ?? line.title ?? '');
    const url = typeof line.url === 'string' ? line.url : '';
    // `image` is the line's own variant image, so a colour-specific shot wins
    // over the product's featured one. Both are protocol-relative CDN URLs.
    const src = sizedImage(line.image ?? line.featured_image?.url, 400);

    const media = this.querySelector('[data-gs-toast-line-media]');
    const image = this.querySelector('[data-gs-toast-line-image]');
    if (media instanceof HTMLAnchorElement) {
      if (url) media.href = url;
      else media.removeAttribute('href');
    }
    if (image instanceof HTMLImageElement) {
      // Not `src = ''`: an empty src resolves to the page URL and re-downloads
      // the document as if it were an image.
      if (src) image.src = src;
      else image.removeAttribute('src');
      image.alt = '';
    }

    const name = this.querySelector('[data-gs-toast-line-title]');
    if (name instanceof HTMLAnchorElement) {
      name.textContent = title;
      if (url) name.href = url;
      else name.removeAttribute('href');
    }

    // `options_with_values` keeps the option's own name, which is what tells a
    // colour apart from a size without guessing at the variant title's order.
    const options = Array.isArray(line.options_with_values) ? line.options_with_values : [];
    const sizeOption = options.find((option) => /siz|beden/i.test(String(option?.name ?? '')));
    const others = options.filter((option) => option !== sizeOption);
    const variantText = others.map((option) => String(option?.value ?? '')).filter(Boolean).join(' / ');
    const sizeText = sizeOption ? String(sizeOption.value ?? '') : '';
    const fallback =
      !options.length && typeof line.variant_title === 'string' && line.variant_title !== 'Default Title'
        ? line.variant_title
        : '';

    const variantEl = this.querySelector('[data-gs-toast-line-variant]');
    if (variantEl instanceof HTMLElement) {
      const text = variantText || fallback;
      variantEl.textContent = text;
      variantEl.hidden = !text;
    }

    const sizeEl = this.querySelector('[data-gs-toast-line-size]');
    if (sizeEl instanceof HTMLElement) {
      sizeEl.textContent = sizeText;
      sizeEl.hidden = !sizeText;
    }

    const priceEl = this.querySelector('[data-gs-toast-line-price]');
    if (priceEl instanceof HTMLElement) {
      const cents = Number(line.final_line_price ?? line.line_price ?? line.final_price ?? line.price);
      priceEl.textContent = Number.isFinite(cents) ? this.#formatMoney(cents) : '';
    }

    section.hidden = false;
  }

  /**
   * The free-delivery bar. Unlike the reference's, which is a membership perk
   * and absent for a guest, this one is shown to everyone — the threshold and
   * all four strings come from `snippets/giesto-free-shipping.liquid`, the one
   * place that owns them.
   * @param {{ total?: number }} cart
   */
  #renderDelivery(cart) {
    const host = this.querySelector('[data-gs-toast-delivery]');
    if (!(host instanceof HTMLElement)) return;

    if (this.#delivery === null) {
      const node = this.querySelector('[data-gs-toast-delivery-config]');
      try {
        this.#delivery = node ? JSON.parse(node.textContent || '{}') : {};
      } catch {
        this.#delivery = {};
      }
    }

    const config = this.#delivery ?? {};
    const threshold = Number(config.threshold);
    const total = Number(cart?.total);

    // A zero threshold is the snippet reporting "no promise to make here" —
    // the threshold is in a currency this cart is not priced in. The shipping
    // guard is applied here rather than in Liquid because the config is
    // rendered with the page, when the cart is usually still empty and
    // `requires_shipping` is false for that reason alone.
    if (cart?.requiresShipping === false) {
      host.hidden = true;
      return;
    }

    if (!Number.isFinite(threshold) || threshold <= 0 || !Number.isFinite(total)) {
      host.hidden = true;
      return;
    }

    const remaining = Math.max(threshold - total, 0);
    const reached = remaining === 0;

    const label = this.querySelector('[data-gs-toast-delivery-label]');
    if (label instanceof HTMLElement) {
      label.textContent = reached
        ? String(config.reached ?? '')
        : String(config.pending ?? '').replace('{amount}', this.#formatMoney(remaining));
    }

    // Reaching the threshold strikes what was going to be charged and sets the
    // new value beside it; nothing else about the type changes.
    const was = this.querySelector('[data-gs-toast-delivery-was]');
    if (was instanceof HTMLElement) {
      was.textContent = String(config.pendingValue ?? '');
      was.hidden = !reached;
    }

    const value = this.querySelector('[data-gs-toast-delivery-value]');
    if (value instanceof HTMLElement) {
      value.textContent = String((reached ? config.reachedValue : config.pendingValue) ?? '');
    }

    const fill = this.querySelector('[data-gs-toast-delivery-fill]');
    if (fill instanceof HTMLElement) {
      const share = Math.min(Math.max(total / threshold, 0), 1);
      fill.style.width = `${(share * 100).toFixed(2)}%`;
    }

    host.hidden = false;
  }

  /** @param {number} count */
  #setCount(count) {
    const target = this.querySelector('[data-gs-toast-count]');
    if (target) target.textContent = `(${count})`;
  }

  /**
   * Pulls the resolved event's cart payload when available. A direct fetch is
   * only the compatibility fallback for third-party cart events that do not
   * expose their resulting cart lines.
   *
   * @param {any} result
   * @returns {Promise<{ items: any[], count: number }>}
   */
  async #getCartSnapshot(result) {
    const eventItems = result?.detail?.items;
    if (Array.isArray(eventItems)) {
      const eventCount = Number(result?.cart?.totalQuantity);
      const count = Number.isFinite(eventCount)
        ? eventCount
        : eventItems.reduce((total, item) => total + Math.max(Number(item?.quantity) || 0, 0), 0);

      // The delivery bar needs the cart's money total in cents. `total_price`
      // already is; `cost.totalAmount.amount` is the Storefront API's decimal
      // string in currency units, so it has to be scaled. Getting this the
      // wrong way round shows the shopper a hundredfold error.
      const totalCents = Number(result?.cart?.total_price);
      const totalAmount = Number(result?.cart?.cost?.totalAmount?.amount);
      const total = Number.isFinite(totalCents)
        ? totalCents
        : Number.isFinite(totalAmount)
          ? Math.round(totalAmount * 100)
          : eventItems.reduce(
              (sum, item) => sum + Math.max(Number(item?.final_line_price ?? item?.line_price) || 0, 0),
              0,
            );

      return { items: eventItems, count, total, requiresShipping: result?.cart?.requires_shipping };
    }

    const cart = await this.#fetchCart().catch(() => null);
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const cartCount = Number(cart?.item_count);
    const count = Number.isFinite(cartCount)
      ? cartCount
      : items.reduce((total, item) => total + Math.max(Number(item?.quantity) || 0, 0), 0);
    const cartTotal = Number(cart?.total_price);
    const total = Number.isFinite(cartTotal)
      ? cartTotal
      : items.reduce((sum, item) => sum + Math.max(Number(item?.final_line_price ?? item?.line_price) || 0, 0), 0);

    return { items, count, total, requiresShipping: cart?.requires_shipping };
  }

  /**
   * @param {any} result
   * @param {number} token
   */
  async #refreshCartPreview(result, token) {
    const cart = await this.#getCartSnapshot(result);
    if (token !== this.#token || this.hidden) return;

    this.#setCount(cart.count);
    this.#renderBagItems(cart.items);
    if (cart.count === 0) this.#hide();
  }

  /* ------------------------------------------------------------- clicks -- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-gs-toast-close]') || event.target.closest('[data-gs-toast-dimmer]')) {
      this.#hide();
      return;
    }

    const viewBag = event.target.closest('[data-gs-toast-view]');
    if (viewBag) {
      this.#openCart();
      return;
    }

    const bagPrev = event.target.closest('[data-gs-toast-bag-prev]');
    const bagNext = event.target.closest('[data-gs-toast-bag-next]');
    if (bagPrev || bagNext) {
      this.#scrollBagByCard(bagNext ? 1 : -1);
      return;
    }

    const prev = event.target.closest('[data-gs-toast-prev]');
    const next = event.target.closest('[data-gs-toast-next]');
    if (prev || next) {
      this.#scrollByCard(next ? 1 : -1);
      return;
    }

    const add = event.target.closest('[data-gs-card-add]');
    if (add instanceof HTMLButtonElement) this.#addFromCard(add);
  };

  /**
   * Opens the cart.
   *
   * When a drawer exists the button also carries the theme's own
   * `on:click="#cart-drawer/open"`, and that delegation runs in the CAPTURE
   * phase on `document` — i.e. before this bubble-phase handler. So by the time
   * we get here the drawer is already opening, and calling `open()` again would
   * hit `#bringToFront()` and replay the animation.
   *
   * The one case the declarative binding cannot cover is the upgrade race:
   * `component.js` requires the target to be a `Component`, and its rescue
   * `customElements.upgrade()` only fires for tags ending in `-component`.
   * `<theme-drawer>` does not, so the call is silently dropped while the module
   * is still loading. `typeof drawer.open !== 'function'` detects exactly that
   * window — and only that window — so the drawer is never opened twice.
   */
  #openCart() {
    if (document.querySelector('.ghs-cart')) {
      document.dispatchEvent(new CustomEvent('giesto:cart:open', { detail: { trigger: this.querySelector('[data-gs-toast-view]') } }));
      this.#hide();
      return;
    }
    const drawer =
      this.dataset.hasCartDrawer === 'true'
        ? /** @type {(HTMLElement & { open?: () => void }) | null} */ (
            document.querySelector('theme-drawer#cart-drawer')
          )
        : null;

    if (!drawer) {
      window.location.href = this.dataset.cartUrl || '/cart';
      return;
    }

    if (typeof drawer.open !== 'function') {
      customElements.upgrade(drawer);

      if (typeof drawer.open === 'function') {
        drawer.open();
      } else {
        window.location.href = this.dataset.cartUrl || '/cart';
        return;
      }
    }

    this.#hide();
  }

  /* ---------------------------------------------------------- carousel -- */

  /** @param {number} direction */
  #scrollByCard(direction) {
    this.#scrollCarousel(this.#carousel, '.gs-toast__slide', direction);
  }

  /** @param {number} direction */
  #scrollBagByCard(direction) {
    this.#scrollCarousel(this.#bagCarousel, '.gs-toast__bag-slide', direction);
  }

  /**
   * @param {HTMLElement | null} carousel
   * @param {string} slideSelector
   * @param {number} direction
   */
  #scrollCarousel(carousel, slideSelector, direction) {
    if (!carousel) return;

    const slide = carousel.querySelector(slideSelector);
    const columnGap = Number.parseFloat(getComputedStyle(carousel).columnGap) || 0;
    const step = slide instanceof HTMLElement ? slide.getBoundingClientRect().width + columnGap : carousel.clientWidth / 2;
    const behavior = this.#reducedMotion.matches ? 'auto' : 'smooth';

    carousel.scrollBy({ left: step * direction, behavior });
  }

  #syncNavState = () => {
    this.#syncCarouselNav(
      this.#carousel,
      this.querySelector('[data-gs-toast-prev]'),
      this.querySelector('[data-gs-toast-next]')
    );
  };

  #syncBagNavState = () => {
    this.#syncCarouselNav(
      this.#bagCarousel,
      this.querySelector('[data-gs-toast-bag-prev]'),
      this.querySelector('[data-gs-toast-bag-next]')
    );
  };

  #syncAllNavStates = () => {
    this.#syncNavState();
    this.#syncBagNavState();
  };

  /**
   * @param {HTMLElement | null} carousel
   * @param {Element | null} prev
   * @param {Element | null} next
   */
  #syncCarouselNav(carousel, prev, next) {
    if (!carousel || !(prev instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    const max = carousel.scrollWidth - carousel.clientWidth;
    // `scrollLeft` is negative in RTL and fractional after a snap, so compare
    // against a 1px tolerance on the absolute offset.
    const offset = Math.abs(carousel.scrollLeft);

    prev.disabled = offset <= 1;
    next.disabled = max <= 1 || offset >= max - 1;
  }

  /* ------------------------------------------------------- current bag -- */

  /**
   * @param {any[]} items
   * @param {string} [addedKey]
   */
  #renderBagItems(items, addedKey = '') {
    const bag = this.#bag;
    const carousel = this.#bagCarousel;
    const template = this.#bagCardTemplate;
    if (!bag || !carousel || !template) return;

    if (items.length === 0) {
      carousel.replaceChildren();
      delete carousel.dataset.count;
      bag.setAttribute('hidden', '');
      this.#syncBagNavState();
      return;
    }

    // The toast already identifies the latest line above. Keeping that same
    // line first in the rail guarantees a subsequent add remains visible even
    // when the cart holds more than two items.
    const addedIndex = addedKey ? items.findIndex((item) => String(item?.key ?? '') === addedKey) : -1;
    const orderedItems =
      addedIndex > 0
        ? [items[addedIndex], ...items.slice(0, addedIndex), ...items.slice(addedIndex + 1)]
        : items;

    const fragment = document.createDocumentFragment();
    for (const item of orderedItems) {
      const card = this.#buildBagCard(item, template);
      if (card) fragment.append(card);
    }

    const hadFocus = carousel.contains(document.activeElement);
    carousel.replaceChildren(fragment);
    carousel.dataset.count = String(orderedItems.length);
    carousel.scrollTo({ left: 0, behavior: 'auto' });
    bag.toggleAttribute('hidden', orderedItems.length === 0);

    if (hadFocus) this.#panel?.focus({ preventScroll: true });
    requestAnimationFrame(this.#syncBagNavState);
  }

  /**
   * @param {any} item
   * @param {HTMLTemplateElement} template
   * @returns {HTMLElement | null}
   */
  #buildBagCard(item, template) {
    const slide = template.content.querySelector('.gs-toast__bag-slide')?.cloneNode(true);
    if (!(slide instanceof HTMLElement)) return null;

    const title = String(item?.product_title ?? item?.title ?? '');
    const url = String(item?.url ?? '');
    const image = sizedImage(item?.image ?? item?.featured_image?.url, 160);
    const variantTitle = String(item?.variant_title ?? '').trim();
    const quantity = Math.max(Number(item?.quantity) || 0, 0);
    const quantityLabel = String(this.dataset.cartQuantityLabel ?? '').trim();

    const card = slide.querySelector('.gs-toast__bag-card');
    card?.toggleAttribute('data-no-image', !image);

    const link = slide.querySelector('[data-gs-toast-bag-link]');
    if (link instanceof HTMLAnchorElement) {
      if (url) link.href = url;
      else {
        link.removeAttribute('href');
        link.tabIndex = -1;
      }
    }

    const media = slide.querySelector('[data-gs-toast-bag-media]');
    const imageEl = slide.querySelector('[data-gs-toast-bag-image]');
    if (media instanceof HTMLElement) media.toggleAttribute('hidden', !image);
    if (imageEl instanceof HTMLImageElement) {
      if (image) imageEl.src = image;
      else imageEl.removeAttribute('src');
    }

    const titleEl = slide.querySelector('[data-gs-toast-bag-title]');
    if (titleEl) titleEl.textContent = title;

    const meta = slide.querySelector('[data-gs-toast-bag-meta]');
    if (meta instanceof HTMLElement) {
      const hasNamedVariant = variantTitle && variantTitle.toLowerCase() !== 'default title';
      const quantityText = quantity > 0 ? [quantityLabel, quantity].filter(Boolean).join(' ') : '';
      const parts = [hasNamedVariant ? variantTitle : '', quantityText].filter(Boolean);
      meta.textContent = parts.join(' · ');
      meta.toggleAttribute('hidden', parts.length === 0);
    }

    const price = Number(item?.final_line_price ?? item?.line_price ?? item?.final_price ?? item?.price);
    const priceEl = slide.querySelector('[data-gs-toast-bag-price]');
    if (priceEl instanceof HTMLElement) {
      const hasPrice = Number.isFinite(price);
      priceEl.textContent = hasPrice ? formatMoney(price, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'USD') : '';
      priceEl.toggleAttribute('hidden', !hasPrice);
    }

    return slide;
  }

  /* --------------------------------------------------------- cross-sell -- */

  #clearCrossSell() {
    this.#entries.clear();
    const carousel = this.#carousel;
    if (carousel) carousel.replaceChildren();
    this.#cross?.setAttribute('hidden', '');
  }

  /**
   * @param {string} productId
   * @param {number} token
   */
  async #loadRecommendations(productId, token) {
    const route = this.dataset.recommendationsUrl;
    const template = this.#cardTemplate;
    const carousel = this.#carousel;
    if (!route || !template || !carousel) return;

    const limit = Math.min(Math.max(Number(this.dataset.recommendationsLimit) || 6, 1), 10);
    const poolLimit = Math.min(Math.max(Number(this.dataset.recommendationsPoolLimit) || 10, limit), 10);
    const endpoint = route.endsWith('.json') ? route : `${route.replace(/\/$/, '')}.json`;

    let url;
    try {
      url = new URL(endpoint, window.location.origin);
    } catch {
      return;
    }

    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', String(poolLimit));
    url.searchParams.set('intent', RECOMMENDATION_INTENT);

    /** @type {any[]} */
    let products = [];
    try {
      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: this.#abortController?.signal,
      });
      if (!response.ok) throw new Error(`Recommendations request failed with ${response.status}`);
      const payload = await response.json();
      products = Array.isArray(payload?.products) ? payload.products : [];
    } catch {
      products = [];
    }

    if (token !== this.#token) return;

    const cards = products.filter((product) => this.#isRecommendation(product, productId)).slice(0, limit);

    if (cards.length < limit) {
      const seen = new Set(cards.map((product) => String(product.id)));
      const fallbackProducts = await this.#loadFallbackProducts();
      if (token !== this.#token) return;

      for (const product of fallbackProducts) {
        if (!this.#isRecommendation(product, productId)) continue;
        if (seen.has(String(product.id))) continue;

        cards.push(product);
        seen.add(String(product.id));
        if (cards.length >= limit) break;
      }
    }

    if (cards.length === 0) {
      this.#clearCrossSell();
      return;
    }

    this.#entries.clear();
    const fragment = document.createDocumentFragment();
    for (const product of cards) {
      const slide = this.#buildCard(product, template);
      if (slide) fragment.append(slide);
    }

    carousel.replaceChildren(fragment);
    carousel.scrollTo({ left: 0, behavior: 'auto' });
    this.#cross?.toggleAttribute('hidden', false);
    this.#syncNavState();
  }

  /** @param {any} product */
  #isRecommendation(product, productId) {
    return Boolean(product) && String(product.id) !== productId;
  }

  async #loadFallbackProducts() {
    if (this.#fallbackProducts) return this.#fallbackProducts;

    const route = this.dataset.recommendationsFallbackUrl;
    if (route) {
      try {
        const url = new URL(`${route.replace(/\/$/, '')}/products.json`, window.location.origin);
        url.searchParams.set('limit', '250');
        url.searchParams.set('sort_by', 'created-descending');

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: this.#abortController?.signal,
        });
        if (!response.ok) throw new Error(`Fallback recommendations request failed with ${response.status}`);

        const payload = await response.json();
        if (Array.isArray(payload?.products)) {
          this.#fallbackProducts = payload.products;
          return this.#fallbackProducts;
        }
      } catch {}
    }

    const template = this.querySelector('[data-gs-toast-fallback-products]');
    if (!(template instanceof HTMLScriptElement)) return [];

    try {
      const products = JSON.parse(template.textContent || '[]');
      this.#fallbackProducts = Array.isArray(products) ? products : [];
      return this.#fallbackProducts;
    } catch {
      return [];
    }
  }

  /**
   * @param {any} product
   * @param {HTMLTemplateElement} template
   * @returns {HTMLElement | null}
   */
  #buildCard(product, template) {
    const slide = template.content.querySelector('.gs-toast__slide')?.cloneNode(true);
    if (!(slide instanceof HTMLElement)) return null;

    const key = String(product.id);
    const title = String(product.title ?? '');
    const url = String(product.url ?? '');
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const available = variants.filter((variant) => variant?.available);
    // Only a single-variant, in-stock product can be bagged straight from the
    // toast. Anything else has to route through the PDP so the shopper picks.
    const soleVariant = variants.length === 1 && available.length === 1 ? available[0] : null;

    this.#entries.set(key, { product, variant: soleVariant });

    const image = sizedImage(product.featured_image ?? product.images?.[0], 600);
    const imageEl = slide.querySelector('[data-gs-card-image]');
    if (imageEl instanceof HTMLImageElement) {
      // The media link is `aria-hidden` and the title carries the accessible
      // name, so the image is decorative — keep `alt` empty.
      if (image) imageEl.src = image;
      else imageEl.remove();
    }

    for (const link of slide.querySelectorAll('[data-gs-card-media], [data-gs-card-info]')) {
      if (link instanceof HTMLAnchorElement && url) link.href = url;
    }

    const titleEl = slide.querySelector('[data-gs-card-title]');
    if (titleEl) titleEl.textContent = title;

    this.#applyPrice(slide, product);
    this.#applyActions(slide, product, title, url, soleVariant);

    return slide;
  }

  /**
   * @param {HTMLElement} slide
   * @param {any} product
   */
  #applyPrice(slide, product) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    const price = Number(product.price ?? 0);
    const compareAt = Number(product.compare_at_price ?? 0);
    const onSale = compareAt > price;

    const value = slide.querySelector('[data-gs-card-value]');
    if (value) value.textContent = formatMoney(price, format, currency);

    const compare = slide.querySelector('[data-gs-card-compare]');
    if (compare) {
      compare.textContent = onSale ? formatMoney(compareAt, format, currency) : '';
      compare.toggleAttribute('hidden', !onSale);
    }

    slide.querySelector('[data-gs-card-price]')?.classList.toggle('gs-toast__card-price--sale', onSale);
  }

  /**
   * @param {HTMLElement} slide
   * @param {any} product
   * @param {string} title
   * @param {string} url
   * @param {any} soleVariant
   */
  #applyActions(slide, product, title, url, soleVariant) {
    const token = this.dataset.titleToken || '__ARK_TITLE__';

    const add = slide.querySelector('[data-gs-card-add]');
    if (add instanceof HTMLButtonElement) {
      const canAdd = Boolean(soleVariant);
      const label = canAdd ? this.dataset.addLabel || 'Add to cart' : this.dataset.chooseLabel || 'Choose';
      const text = `${label}: ${title}`;

      add.setAttribute('aria-label', text);
      add.disabled = !canAdd && !url;
      add.dataset.gsCardKey = String(product.id);
      add.dataset.gsCardUrl = canAdd ? '' : url;

      const srLabel = slide.querySelector('[data-gs-card-add-label]');
      if (srLabel) srLabel.textContent = text;
    }

    const fav = slide.querySelector('[data-gs-card-fav]');
    if (fav instanceof HTMLElement) {
      const format = this.dataset.moneyFormat || '{{amount}}';
      const currency = this.dataset.currency || 'USD';

      fav.dataset.productId = String(product.id);
      fav.dataset.productHandle = String(product.handle ?? '');
      fav.dataset.productTitle = title;
      fav.dataset.productUrl = url;
      fav.dataset.productImage = sizedImage(product.featured_image ?? product.images?.[0], 400);
      fav.dataset.productPrice = formatMoney(Number(product.price ?? 0), format, currency);
      fav.dataset.labelAdd = (this.dataset.wishlistAddLabel || 'Add {{ title }} to favourites').replaceAll(
        token,
        title
      );
      fav.dataset.labelRemove = (this.dataset.wishlistRemoveLabel || 'Remove {{ title }} from favourites').replaceAll(
        token,
        title
      );

      const favButton = fav.querySelector('button');
      favButton?.setAttribute('aria-label', fav.dataset.labelAdd);
    }
  }

  /* ---------------------------------------------------------- cart client -- */

  /**
   * Adds one variant to the cart.
   *
   * PUBLIC ON PURPOSE. `assets/giesto-store-size-picker.js` calls this instead
   * of shipping a second cart client, so the storefront has exactly one
   * place that knows about `/cart/add`, the `sections` piggyback that lets
   * `cart-items-component` morph the drawer and cart page in place, and the
   * optimistic `CartLinesUpdateEvent` / `CartErrorEvent` contract. The toast's
   * own listener for that event is what then presents the panel, so callers get
   * the confirmation for free.
   *
   * @param {object} options
   * @param {string | number} options.variantId - Variant to add.
   * @param {number} [options.quantity] - Defaults to 1.
   * @param {string | number | null} [options.productId] - Reported back in the resolved detail.
   * @param {HTMLElement} [options.source] - Element the cart events are dispatched from, so the
   *   theme's pending states land on the control the shopper actually pressed. Defaults to the toast.
   * @returns {Promise<void>} Resolves once the cart has been re-read.
   * @throws {Error} If the cart rejects the line. A `CartErrorEvent` has already been dispatched.
   */
  async addVariant({ variantId, quantity = 1, productId = null, source } = {}) {
    const target = source instanceof HTMLElement ? source : this;

    const formData = new FormData();
    formData.set('id', String(variantId));
    formData.set('quantity', String(quantity));

    // Ask for the cart sections back so `cart-items-component` can morph the
    // drawer and cart page in place, exactly like the theme's own add paths.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length > 0) formData.set('sections', sectionIds.join(','));
    formData.set('sections_url', window.location.pathname);

    const deferred = CartLinesUpdateEvent.createPromise();

    // Dispatched before the request so the cart icon and quantity controls
    // enter their pending state, matching `assets/giesto-cross-sell.js`.
    target.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await this.#postCartAdd(formData);
      const cart = await this.#fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          itemCount: quantity,
          source: 'giesto-store-toast',
          productId,
          sections: response.sections,
        },
      });
    } catch (error) {
      deferred.reject(error);
      const message = error instanceof Error ? error.message : this.dataset.errorLabel || 'Error adding to cart';
      target.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
      throw error;
    }
  }

  /* ------------------------------------------------------- add from card -- */

  /** @param {HTMLButtonElement} button */
  async #addFromCard(button) {
    if (button.dataset.busy === 'true') return;

    const fallbackUrl = button.dataset.gsCardUrl;
    const entry = this.#entries.get(button.dataset.gsCardKey ?? '');
    const variant = entry?.variant;

    if (!variant) {
      if (fallbackUrl) window.location.href = fallbackUrl;
      return;
    }

    button.dataset.busy = 'true';
    button.disabled = true;

    try {
      await this.addVariant({ variantId: variant.id, productId: entry?.product?.id, source: button });
    } catch {
      // `addVariant` has already dispatched the CartErrorEvent the theme
      // surfaces; there is nothing left for this card to say.
    } finally {
      delete button.dataset.busy;
      button.disabled = false;
    }
  }

  /* ------------------------------------------------------------ network -- */

  /** @param {FormData} formData */
  async #postCartAdd(formData) {
    const route = jsonRoute(this.dataset.cartAddUrl || globalThis.Theme?.routes?.cart_add_url, '/cart/add');

    const response = await fetch(route, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: formData,
      signal: this.#abortController?.signal,
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok || payload?.status || payload?.errors) {
      throw new Error(payload?.description || payload?.message || this.dataset.errorLabel || 'Error adding to cart');
    }

    return payload;
  }

  async #fetchCart() {
    const route = jsonRoute(this.dataset.cartUrl || globalThis.Theme?.routes?.cart_url, '/cart');

    const response = await fetch(route, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: this.#abortController?.signal,
    });

    if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);

    return response.json();
  }
}

if (!customElements.get('giesto-store-toast')) {
  customElements.define('giesto-store-toast', GiestoStoreToast);
}
